import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { ACTIVE_PIN_COLOR, CUSTOM_MAP_STYLE, DEFAULT_CARS_COUNT, DEFAULT_MIAMI_CENTER, DEFAULT_PIN_COLOR, DEFAULT_SCHEDULE, DEFAULT_TOTAL_PRICE, FIXED_ZOOM, } from "./interfaces/mapContent.constants";
import { t } from "i18next";
const getLocationKey = (location) => location.locationCode || location.address;
export const MapContent = ({ locations, setLocations, mapId = "default-map", onlyShowMap = false, visibleLocationKeys, }) => {
    var _a, _b, _c;
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef(new Map());
    const hasCenteredRef = useRef(false);
    const [activeLocation, setActiveLocation] = useState(null);
    // 1) Initialize the map only once
    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current)
            return;
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
            zoom: FIXED_ZOOM,
            center: DEFAULT_MIAMI_CENTER,
            disableDefaultUI: false,
            zoomControl: false,
            styles: CUSTOM_MAP_STYLE,
            fullscreenControl: false,
            streetViewControl: false,
            cameraControl: false,
            mapTypeControl: false,
        });
    }, []);
    // 2) Sync markers and keep center behavior stable
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map)
            return;
        const visibleKeys = new Set(visibleLocationKeys || locations.map(getLocationKey));
        const visibleLocations = locations.filter((location) => visibleKeys.has(getLocationKey(location)));
        const activeLocation = visibleLocations.find((location) => location.active) || null;
        setActiveLocation(activeLocation);
        // Pan to active location whenever one is set
        if (activeLocation && typeof activeLocation.lat === "number" && typeof activeLocation.lng === "number") {
            map.panTo({ lat: activeLocation.lat, lng: activeLocation.lng });
            hasCenteredRef.current = true;
        }
        else if (!hasCenteredRef.current) {
            // Center on the first available location on initial render
            const firstWithCoords = visibleLocations.find((loc) => typeof loc.lat === "number" && typeof loc.lng === "number");
            if (firstWithCoords) {
                map.setCenter({
                    lat: firstWithCoords.lat,
                    lng: firstWithCoords.lng,
                });
                hasCenteredRef.current = true;
            }
        }
        const nextKeys = new Set();
        locations.forEach((location) => {
            if (typeof location.lat !== "number" || typeof location.lng !== "number")
                return;
            const key = getLocationKey(location);
            nextKeys.add(key);
            const markerEntry = markersRef.current.get(key);
            if (!markerEntry) {
                addMarker(location, {
                    lat: location.lat,
                    lng: location.lng,
                });
            }
            else {
                markersRef.current.set(key, {
                    ...markerEntry,
                    location,
                });
            }
        });
        markersRef.current.forEach(({ marker }, key) => {
            if (!nextKeys.has(key)) {
                marker.setMap(null);
                markersRef.current.delete(key);
            }
        });
        // Rebind marker listeners
        markersRef.current.forEach(({ location, marker }, key) => {
            google.maps.event.clearListeners(marker, "click");
            if (!visibleKeys.has(key)) {
                marker.setMap(null);
                return;
            }
            marker.setMap(map);
            marker.addListener("click", () => {
                markersRef.current.forEach(({ marker: otherMarker, location: otherLocation }, otherKey) => {
                    if (otherKey === key)
                        return;
                    otherMarker.setIcon(createPinIcon(DEFAULT_PIN_COLOR, otherLocation.countLocation || 1));
                });
                marker.setIcon(createPinIcon(ACTIVE_PIN_COLOR, location.countLocation || 1));
                onClickMarker(location);
            });
        });
        // Sync marker colors with the current active state from locations prop
        markersRef.current.forEach(({ marker }, key) => {
            const updated = locations.find((loc) => getLocationKey(loc) === key);
            if (!updated)
                return;
            marker.setIcon(createPinIcon(updated.active ? ACTIVE_PIN_COLOR : DEFAULT_PIN_COLOR, updated.countLocation || 1));
        });
    }, [locations, visibleLocationKeys]);
    const createPinIcon = (color, count = 1) => {
        if (count === 1) {
            return {
                path: "M12 0.5C18.3401 0.5 23.5 5.65994 23.5 12C23.5 16.0177 21.691 20.3419 18.2109 24.8438H18.21C16.9201 26.5116 15.6354 27.897 14.6689 28.8672C14.1856 29.3524 13.7808 29.7352 13.4941 29.998C13.3509 30.1294 13.2364 30.2315 13.1562 30.3018C13.1163 30.3368 13.0841 30.3648 13.0605 30.3848C13.049 30.3945 13.0377 30.4035 13.0283 30.4111C13.024 30.4147 13.0176 30.4195 13.0107 30.4248C13.0096 30.4257 13.0077 30.4264 13.0059 30.4277C12.7188 30.6762 12.3606 30.7939 12 30.7939C11.6247 30.7939 11.2505 30.6683 10.958 30.3984L10.8906 30.3359C10.8775 30.3242 10.8622 30.3106 10.8447 30.2949C10.7684 30.2266 10.6565 30.1262 10.5146 29.9961C10.2308 29.7357 9.82738 29.3567 9.34375 28.874C8.37698 27.9091 7.0871 26.5265 5.78906 24.8428C2.30935 20.3411 0.5 16.0175 0.5 12C0.500001 5.65994 5.65994 0.5 12 0.5ZM12 6.96387C9.20024 6.96387 6.91992 9.24419 6.91992 12.0439C6.91992 14.8446 9.18645 17.124 12 17.124C14.7998 17.124 17.0801 14.8437 17.0801 12.0439C17.0801 9.24419 14.7998 6.96387 12 6.96387Z",
                fillColor: color,
                fillOpacity: 1,
                strokeWeight: 0,
                scale: 1,
                anchor: new google.maps.Point(12, 22),
            };
        }
        return {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
            scale: 16,
            labelOrigin: new google.maps.Point(0, 0), // centro REAL del circle
        };
    };
    const onClickMarker = (location) => {
        setLocations === null || setLocations === void 0 ? void 0 : setLocations((previous) => previous.map((loc) => ({
            ...loc,
            active: loc.locationCode === location.locationCode,
        })));
    };
    const addMarker = (location, position) => {
        const map = mapInstanceRef.current;
        if (!map)
            return;
        const { countLocation = 1, active } = location;
        const key = getLocationKey(location);
        const propMarker = {
            position,
            map,
            icon: createPinIcon(active ? ACTIVE_PIN_COLOR : DEFAULT_PIN_COLOR, countLocation),
        };
        if (countLocation > 1) {
            propMarker.label = {
                text: String(countLocation),
                color: "#FFFFFF",
                fontSize: "14px",
                fontWeight: "bold",
            };
        }
        const marker = new google.maps.Marker(propMarker);
        markersRef.current.set(key, {
            location,
            marker,
        });
    };
    const onCloseActiveLocation = () => {
        setLocations === null || setLocations === void 0 ? void 0 : setLocations((previous) => previous.map((loc) => ({
            ...loc,
            active: false,
            selected: false,
        })));
    };
    const onSelectedLocation = () => {
        setLocations === null || setLocations === void 0 ? void 0 : setLocations((previous) => previous.map((loc) => ({
            ...loc,
            selected: loc.locationCode === (activeLocation === null || activeLocation === void 0 ? void 0 : activeLocation.locationCode),
        })));
    };
    // Inject overlay style for map-only mode
    useEffect(() => {
        const style = document.createElement("style");
        style.innerHTML = `
      #${mapId}.map-overlay::before {
        content: "";
        position: absolute;
        inset: 0;
        background: rgba(0,0,0,0);
        z-index: 10;
        pointer-events: all;
      }
    `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, [mapId]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { id: mapId, ref: mapRef, className: onlyShowMap ? "map-overlay" : "", style: {
                    width: "100%",
                    height: "100%",
                    position: "relative",
                } }), activeLocation && (_jsx("div", { id: mapId + "-info-window", className: "locations-modal-desktop__fixed-modal", children: _jsxs("div", { className: "map-info-window", children: [_jsx("button", { className: "map-info-close", onClick: onCloseActiveLocation, children: "\u2715" }), _jsx("div", { className: "map-info-renters", children: (_a = activeLocation.brands) === null || _a === void 0 ? void 0 : _a.map((brand, index) => (_jsx("img", { src: brand, alt: "brand", loading: "lazy" }, `brand-${index}`))) }), _jsxs("div", { className: "map-info-block", children: [_jsx("h3", { className: "map-info-title", children: activeLocation.title }), _jsx("p", { className: "map-info-text", children: activeLocation.address }), _jsx("p", { className: "map-info-text", children: activeLocation.schedules || DEFAULT_SCHEDULE })] }), _jsxs("div", { className: "map-info-block", children: [_jsxs("p", { className: "map-info-text", style: { visibility: Number(activeLocation.carsCount) === 0 ? "hidden" : "visible" }, children: [t("availability-label"), ":", " ", _jsxs("strong", { children: [activeLocation.carsCount || DEFAULT_CARS_COUNT, " ", t("cars-label")] })] }), _jsxs("p", { className: "map-info-text", style: { visibility: Number((_b = activeLocation === null || activeLocation === void 0 ? void 0 : activeLocation.price) === null || _b === void 0 ? void 0 : _b.replace(/[^0-9.-]+/g, "")) === 0 ? "hidden" : "visible" }, children: [t("total"), ": ", _jsx("strong", { children: (_c = activeLocation === null || activeLocation === void 0 ? void 0 : activeLocation.price) !== null && _c !== void 0 ? _c : DEFAULT_TOTAL_PRICE })] })] }), _jsx("button", { className: `map-info-button ${activeLocation.selected ? "is-hidden" : ""}`, onClick: onSelectedLocation, children: t("btn-select") })] }) }))] }));
};
